/**
 * GQ Support Agent — service layer
 *
 * Live mode:  set REACT_APP_GQ_SUPPORT_API in your .env file, e.g.
 *             REACT_APP_GQ_SUPPORT_API = "http://localhost:8010"
 *             Then call configureGQSupport(email, isAdmin) once on login.
 *
 * Mock mode:  leave REACT_APP_GQ_SUPPORT_API empty (default for dev).
 *             All methods return in-memory seed data with simulated latency.
 */

import { repairMojibake, fixEncoding } from '../utils/fixMojibake';

// ---------------------------------------------------------------------------
// Runtime config — set from GQSupport.js once account info is loaded
// ---------------------------------------------------------------------------

let _userEmail = 'anonymous@aptean.com';
let _isAdmin   = false;

export function configureGQSupport(email, isAdmin) {
  _userEmail = email || 'anonymous@aptean.com';
  _isAdmin   = !!isAdmin;
}

// ---------------------------------------------------------------------------
// Live API helpers
// ---------------------------------------------------------------------------

const BASE    = process.env.REACT_APP_GQ_SUPPORT_API || '';
const isLive  = () => !!BASE;

function headers() {
  return {
    'Content-Type': 'application/json',
    'X-User-Id':    _userEmail,
    'X-User-Role':  _isAdmin ? 'admin' : 'support',
  };
}

function adminHeaders() {
  return {
    'Content-Type': 'application/json',
    'X-User-Id':    _userEmail,
    'X-User-Role':  'admin',
  };
}

async function req(path, options = {}) {
  const res = await fetch(`${BASE}${path}`, { ...options, headers: { ...headers(), ...(options.headers || {}) } });
  if (!res.ok) throw new Error(`GQ Support API error ${res.status}: ${res.statusText}`);
  // Repair any double-encoded text in the response. Safe across every endpoint:
  // it only touches strings with mojibake markers and leaves clean text alone.
  return res.status === 204 ? null : res.json().then(fixEncoding);
}

async function reqAdmin(path, options = {}) {
  const res = await fetch(`${BASE}${path}`, { ...options, headers: { ...adminHeaders(), ...(options.headers || {}) } });
  if (!res.ok) throw new Error(`GQ Support API error ${res.status}: ${res.statusText}`);
  // Admin pages (Q&A Library, Escalation) read stored data that may be
  // double-encoded — repair it on the way in (see utils/fixMojibake).
  return res.status === 204 ? null : res.json().then(fixEncoding);
}

const api = {
  get:    (path)         => req(path),
  post:   (path, body)   => req(path, { method: 'POST',   body: JSON.stringify(body) }),
  put:    (path, body)   => req(path, { method: 'PUT',    body: JSON.stringify(body) }),
  del:    (path)         => req(path, { method: 'DELETE' }),
  upload: (path, files)  => {
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    return fetch(`${BASE}${path}`, {
      method: 'POST',
      headers: { 'X-User-Id': _userEmail, 'X-User-Role': _isAdmin ? 'admin' : 'support' },
      body: fd,
    }).then(r => { if (!r.ok) throw new Error(`GQ Support API error ${r.status}: ${r.statusText}`); return r.json().then(fixEncoding); });
  },
};

const adminApi = {
  get:   (path)       => reqAdmin(path),
  post:  (path, body) => reqAdmin(path, { method: 'POST',   body: JSON.stringify(body) }),
  put:   (path, body) => reqAdmin(path, { method: 'PUT',    body: JSON.stringify(body) }),
  patch: (path, body) => reqAdmin(path, { method: 'PATCH',  body: JSON.stringify(body) }),
  del:   (path)       => reqAdmin(path, { method: 'DELETE' }),
};

// ---------------------------------------------------------------------------
// Mock seed data (used when REACT_APP_GQ_SUPPORT_API is not set)
// ---------------------------------------------------------------------------

const SEED_CONVERSATIONS = [
  {
    id: 'c1', title: 'Reset password for locked user',
    updatedAt: '2026-05-27T10:42:00Z', bucket: 'today',
    messages: [
      { id: 'm1', role: 'user', text: 'A customer is locked out after 5 failed logins. How do I unlock them?' },
      {
        id: 'm2', role: 'assistant',
        text: 'A user account locks automatically after 5 failed sign-in attempts within 15 minutes [1]. As an admin you have two unlock options:\n\n• Open the Administration Panel → Users tab, find the user, and click "Unlock" in the row actions menu [1].\n• Via the API, POST to /admin/users/{id}/unlock with an admin token [2].\n\nThe user can also wait 30 minutes for the lockout to expire automatically [1].',
        citations: [
          { n: 1, file: 'troubleshooting-login.md', section: 'Account lockouts', excerpt: 'Accounts are locked automatically after 5 failed sign-in attempts within a 15-minute window.' },
          { n: 2, file: 'admin-api.md', section: 'POST /admin/users/{id}/unlock', excerpt: 'Unlocks a user account. Requires an admin token with the user.admin scope.' },
        ],
      },
    ],
  },
  { id: 'c2', title: 'Bulk import sequences fails',   updatedAt: '2026-05-27T09:18:00Z', bucket: 'today',     messages: [] },
  { id: 'c3', title: 'Configure SSO with Azure AD',   updatedAt: '2026-05-26T14:00:00Z', bucket: 'yesterday', messages: [] },
  { id: 'c4', title: 'Sequence variation thresholds', updatedAt: '2026-05-26T11:20:00Z', bucket: 'yesterday', messages: [] },
  { id: 'c5', title: 'API rate limits for export',    updatedAt: '2026-05-25T16:45:00Z', bucket: 'last7',     messages: [] },
  { id: 'c6', title: 'Antibody search returns 0 hits',updatedAt: '2026-05-25T09:10:00Z', bucket: 'last7',     messages: [] },
];

const SEED_SOURCES = [
  { id: 's1', name: 'bulk-import.md',              folder: 'Bulk import',     sizeKb: 18, lastIndexed: '12 min ago', status: 'indexed', citations: 241 },
  { id: 's2', name: 'async-import-api.md',         folder: 'Bulk import',     sizeKb: 26, lastIndexed: '12 min ago', status: 'indexed', citations: 187 },
  { id: 's3', name: 'sso-azure-ad.md',             folder: 'Admin & SSO',     sizeKb: 42, lastIndexed: '1 hr ago',   status: 'indexed', citations: 154 },
  { id: 's4', name: 'sequence-variation-rules.md', folder: 'Sequence search', sizeKb: 9,  lastIndexed: '3 hr ago',   status: 'pending', citations: 98  },
  { id: 's5', name: 'antibody-search.md',          folder: 'Sequence search', sizeKb: 31, lastIndexed: 'Yesterday',  status: 'indexed', citations: 87  },
  { id: 's6', name: 'api-rate-limits.md',          folder: 'API reference',   sizeKb: 7,  lastIndexed: 'Yesterday',  status: 'failed',  citations: 0   },
  { id: 's7', name: 'release-2026-02.md',          folder: 'Release notes',   sizeKb: 12, lastIndexed: 'Feb 27',     status: 'indexed', citations: 62  },
  { id: 's8', name: 'troubleshooting-login.md',    folder: 'Admin & SSO',     sizeKb: 14, lastIndexed: 'Feb 14',     status: 'indexed', citations: 128 },
];

const SEED_FOLDERS = [
  { name: 'All sources',     count: 128 },
  { name: 'Getting started', count: 14  },
  { name: 'Sequence search', count: 32  },
  { name: 'Bulk import',     count: 8   },
  { name: 'Admin & SSO',     count: 19  },
  { name: 'API reference',   count: 22  },
  { name: 'Release notes',   count: 33  },
];

const SEED_QUEUE = [
  { id: 'q1', name: 'query-builder-tips.md',     progress: 100, status: 'done',    note: 'Indexed · 12 chunks · 8 sec' },
  { id: 'q2', name: 'release-2026-05.md',        progress: 100, status: 'done',    note: 'Indexed · 9 chunks · 5 sec'  },
  { id: 'q3', name: 'advanced-search-syntax.md', progress: 62,  status: 'running', note: 'Embedding chunk 14 of 22…'   },
  { id: 'q4', name: 'result-viewer.md',          progress: 0,   status: 'queued',  note: 'Waiting · position 1 of 3'   },
  { id: 'q5', name: 'export-csv-rules.md',       progress: 0,   status: 'failed',  note: 'Parse error: unclosed code fence on line 47.' },
];

const SEED_ANALYTICS = {
  kpis: {
    queries:        { value: 1247, delta: '+18% vs last 30d', direction: 'up'  },
    avgResponseSec: { value: 1.8,  delta: '0.3s faster',      direction: 'up'  },
    unanswered:     { value: 43,   delta: 'needs review',     direction: 'bad' },
  },
  series: [
    { d: 'Apr 27', total: 36 }, { d: 'Apr 29', total: 40 },
    { d: 'May 01', total: 42 }, { d: 'May 03', total: 47 },
    { d: 'May 05', total: 45 }, { d: 'May 07', total: 52 },
    { d: 'May 09', total: 50 }, { d: 'May 11', total: 56 },
    { d: 'May 13', total: 58 }, { d: 'May 15', total: 62 },
    { d: 'May 17', total: 60 }, { d: 'May 19', total: 66 },
    { d: 'May 21', total: 64 }, { d: 'May 23', total: 68 },
    { d: 'May 25', total: 70 }, { d: 'May 27', total: 72 },
  ],
  topSources: [
    { name: 'bulk-import.md',              count: 241 }, { name: 'async-import-api.md',      count: 187 },
    { name: 'sso-azure-ad.md',             count: 154 }, { name: 'troubleshooting-login.md', count: 128 },
    { name: 'sequence-variation-rules.md', count: 98  }, { name: 'antibody-search.md',       count: 87  },
    { name: 'release-2026-02.md',          count: 62  },
  ],
  flagged: [
    { id: 'f1', q: "How do I revoke a user's API token immediately?", reason: 'No source matched — 0 citations' },
    { id: 'f2', q: 'Why is sequence variation export blank?',          reason: 'Low confidence (38%)'           },
    { id: 'f3', q: 'Combine Antibody + Patent search in one query',    reason: 'Outdated source (v11 doc)'      },
    { id: 'f4', q: 'Where is the SAML metadata XML stored?',           reason: 'Conflicting citations'          },
    { id: 'f5', q: 'Maximum chemical modifications per query',         reason: 'No source matched'              },
  ],
};

const SEED_SETTINGS = {
  agentName: 'GQ Support',
  greeting: "Hi! I'm GQ Support. Ask me anything about GenomeQuest — I'll answer from the official docs and link the sources.",
  systemPrompt: `You are GQ Support, the internal assistant for the GenomeQuest support team.
Answer using ONLY the provided .md sources. Cite every factual claim with [n].
If the docs don't cover the question, say so plainly and recommend escalation.
Tone: concise, technical, friendly. Prefer bullet lists for steps. Never invent
version numbers, API endpoints, or limits that are not in the sources.`,
  style: 'Concise',
  confidenceThreshold: 60,
};

const SEED_QA_SUGGESTIONS = [
  {
    id: 'sg1',
    question: 'A customer is locked out after 5 failed logins. How do I unlock them?',
    answer:   'A user account locks automatically after 5 failed sign-in attempts within 15 minutes [1]. Unlock from Administration Panel → Users → "Unlock", or POST /admin/users/{id}/unlock [2]. Or wait 30 minutes.',
    citations:[{ n: 1, file: 'troubleshooting-login.md', section: 'Account lockouts' },
               { n: 2, file: 'admin-api.md', section: 'POST /admin/users/{id}/unlock' }],
    submittedBy:'dias.george@aptean.com', submittedAt:'2026-05-27T10:42:00Z', convId:'c1', msgId:'m2',
  },
  {
    id: 'sg2',
    question: 'What are the bulk-import row limits?',
    answer:   'Bulk sequence import enforces 5,000 rows / 25 MB per file [1]. For larger files use the Async Import API [2].',
    citations:[{ n: 1, file: 'bulk-import.md', section: 'Limits and quotas' },
               { n: 2, file: 'async-import-api.md', section: 'Why async' }],
    submittedBy:'akua.akuamoah@aptean.com', submittedAt:'2026-05-26T15:18:00Z', convId:'c2', msgId:'m9',
  },
];

const SEED_QA_ENTRIES = [
  {
    id: 'qa1',
    question: 'How do I reset a forgotten admin password?',
    answer:   'Use the CLI: `gq-admin reset-password <username>`. The new password is printed to stdout and must be changed on first login [1].',
    citations:[{ n: 1, file: 'troubleshooting-login.md', section: 'Admin password reset' }],
    tags:['login', 'admin'], approvedBy:'dias.george@aptean.com', approvedAt:'2026-05-20T14:00:00Z',
  },
];

function qaEntriesToMarkdown(entries) {
  const lines = [
    '# Verified Q&A — GenomeQuest Support', '',
    `_Generated ${new Date().toLocaleString()} · ${entries.length} entries_`, '',
    '---', '',
  ];
  for (const e of entries) {
    lines.push(`## ${e.question}`, '', e.answer, '');
    if (e.citations?.length) lines.push('**Sources:** ' + e.citations.map(c => `\`${c.file}\` — ${c.section}`).join(', '), '');
    lines.push(`**Verified:** ${(e.approvedAt || '').slice(0, 10)} by ${e.approvedBy || 'unknown'}`);
    if (e.tags?.length) lines.push(`**Tags:** ${e.tags.join(', ')}`);
    lines.push('', '---', '');
  }
  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const delay = ms => new Promise(r => setTimeout(r, ms));
function clone(o) { return o === undefined ? null : JSON.parse(JSON.stringify(o)); }

// Mojibake repair (undo double/triple UTF-8->Latin-1 corruption in stored text)
// lives in a shared util so GQ Search reuses the exact same logic:
//   src/utils/fixMojibake.js  ->  repairMojibake(), fixEncoding()

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

const SEED_ESCALATIONS = [
  {
    id: 'e1', convId: 'c2', messageId: 'm2',
    question: 'Bulk import of 8,000 sequences keeps failing at row 5,001 — is there a hard limit?',
    answer: 'The bulk importer enforces a 5,000-row / 25 MB ceiling per file [1]. Split the file or use the Async Import API for larger jobs [2].',
    citations: [
      { n: 1, file: 'bulk-import.md', section: 'Limits and quotas', excerpt: 'Each upload may contain up to 5,000 rows or 25 MB, whichever is smaller.' },
    ],
    note: 'Customer says splitting the file still errors — needs L2 to check their account quota.',
    priority: 'high', status: 'open',
    createdBy: 'support1@aptean.com', emailSent: true,
    createdAt: '2026-06-04T15:10:00Z', updatedAt: '2026-06-04T15:10:00Z',
  },
];

const gqSupportService = {

  // --- Conversations --------------------------------------------------------

  async listConversations() {
    if (isLive()) return api.get('/api/conversations');
    await delay(120);
    return clone(SEED_CONVERSATIONS);
  },

  async getConversation(id) {
    if (isLive()) return api.get(`/api/conversations/${id}`);
    await delay(80);
    const conv = SEED_CONVERSATIONS.find(c => c.id === id);
    return conv
      ? clone(conv)
      : { id, title: 'New conversation', updatedAt: new Date().toISOString(), bucket: 'today', messages: [] };
  },

  async createConversation() {
    // Live: the backend creates the row and returns its real id (a UUID).
    // Posting messages to a client-invented id would 404.
    if (isLive()) return api.post('/api/conversations');
    await delay(60);
    return { id: 'c' + Date.now(), title: 'New conversation', updatedAt: new Date().toISOString(), bucket: 'today', messages: [] };
  },

  async deleteConversation(convId) {
    if (isLive()) return api.del(`/api/conversations/${convId}`);
    await delay(120);
    return { ok: true };
  },

  async sendMessage(convId, text) {
    if (isLive()) return api.post(`/api/conversations/${convId}/messages`, { text });
    await delay(600);
    return {
      id: 'mNew' + Date.now(), role: 'assistant',
      text: `(Mock answer for "${text}")  The bulk importer enforces a 5,000-row / 25 MB ceiling per file [1]. Split the file or use the Async Import API for larger jobs [2].`,
      citations: [
        { n: 1, file: 'bulk-import.md',      section: 'Limits and quotas', excerpt: 'Each upload may contain up to 5,000 rows or 25 MB, whichever is smaller.' },
        { n: 2, file: 'async-import-api.md', section: 'Why async',         excerpt: 'Async Import accepts files up to 500 MB and chunks them server-side.' },
      ],
    };
  },

  async regenerateMessage(convId, messageId) {
    if (isLive()) return api.post(`/api/conversations/${convId}/messages/${messageId}/regenerate`);
    await delay(500);
    return {
      id: 'mRegen' + Date.now(), role: 'assistant',
      text: '(Regenerated mock answer) Bulk import caps at 5,000 rows / 25 MB per file [1]. For larger files use the Async Import API [2].',
      citations: [
        { n: 1, file: 'bulk-import.md',      section: 'Limits and quotas', excerpt: 'Up to 5,000 rows or 25 MB.' },
        { n: 2, file: 'async-import-api.md', section: 'Why async',         excerpt: 'Async Import accepts files up to 500 MB.' },
      ],
    };
  },

  // "Send for approval" — submit (submitted=true) or withdraw (submitted=false)
  // an answer as a Q&A suggestion for admin review. Not a thumbs rating.
  async submitForApproval(convId, messageId, submitted = true) {
    if (isLive()) return api.post(`/api/conversations/${convId}/messages/${messageId}/submit-approval`, { submitted });
    await delay(60);
    return { ok: true };
  },

  // --- Sources --------------------------------------------------------------

  async listSources() {
    if (isLive()) return api.get('/api/sources');
    await delay(140);
    return clone(SEED_SOURCES);
  },

  async listFolders() {
    if (isLive()) return api.get('/api/folders');
    await delay(60);
    return clone(SEED_FOLDERS);
  },

  async reindexSource(id) {
    if (isLive()) return api.post(`/api/sources/${id}/reindex`);
    await delay(200);
    return { ok: true };
  },

  async deleteSource(id) {
    if (isLive()) return api.del(`/api/sources/${id}`);
    await delay(200);
    return { ok: true };
  },

  // --- Upload ---------------------------------------------------------------

  async listQueue() {
    if (isLive()) return api.get('/api/queue');
    await delay(120);
    return clone(SEED_QUEUE);
  },

  async uploadFiles(files) {
    if (isLive()) return api.upload('/api/sources/upload', files);
    await delay(400);
    return { ok: true, accepted: files.length };
  },

  // --- Analytics & Settings -------------------------------------------------

  async getAnalytics(days = 30) {
    if (isLive()) return api.get(`/api/analytics?days=${days}`);
    await delay(180);
    return clone(SEED_ANALYTICS);
  },

  async getSettings() {
    if (isLive()) return api.get('/api/settings');
    await delay(80);
    return clone(SEED_SETTINGS);
  },

  async saveSettings(s) {
    if (isLive()) return api.put('/api/settings', s);
    await delay(220);
    Object.assign(SEED_SETTINGS, s);
    return { ok: true };
  },

  // --- Q&A Library ----------------------------------------------------------

  async listQASuggestions() {
    if (isLive()) return adminApi.get('/api/qa/suggestions');
    await delay(120);
    return clone(SEED_QA_SUGGESTIONS);
  },

  async listQAEntries() {
    if (isLive()) return adminApi.get('/api/qa/entries');
    await delay(120);
    return clone(SEED_QA_ENTRIES);
  },

  // edits = { question, answer, citations, tags } — always send a full payload
  async promoteSuggestion(msgId, edits) {
    if (isLive()) return adminApi.post(`/api/qa/promote/${msgId}`, edits || {});
    await delay(200);
    const idx = SEED_QA_SUGGESTIONS.findIndex(s => s.id === msgId);
    if (idx === -1) return { ok: false };
    const s = SEED_QA_SUGGESTIONS[idx];
    const entry = {
      id: 'qa' + Date.now(),
      question:  edits?.question  ?? s.question,
      answer:    edits?.answer    ?? s.answer,
      citations: edits?.citations ?? s.citations,
      tags:      edits?.tags      ?? [],
      approvedBy: _userEmail,
      approvedAt: new Date().toISOString(),
    };
    SEED_QA_ENTRIES.unshift(entry);
    SEED_QA_SUGGESTIONS.splice(idx, 1);
    return clone(entry);
  },

  async dismissSuggestion(msgId) {
    if (isLive()) return adminApi.post(`/api/qa/suggestions/${msgId}/dismiss`);
    await delay(80);
    const idx = SEED_QA_SUGGESTIONS.findIndex(s => s.id === msgId);
    if (idx !== -1) SEED_QA_SUGGESTIONS.splice(idx, 1);
    return { ok: true };
  },

  async updateQAEntry(id, edits) {
    if (isLive()) return adminApi.put(`/api/qa/entries/${id}`, edits);
    await delay(160);
    const e = SEED_QA_ENTRIES.find(x => x.id === id);
    if (e) Object.assign(e, edits);
    return clone(e);
  },

  async deleteQAEntry(id) {
    if (isLive()) return adminApi.del(`/api/qa/entries/${id}`);
    await delay(120);
    const idx = SEED_QA_ENTRIES.findIndex(x => x.id === id);
    if (idx !== -1) SEED_QA_ENTRIES.splice(idx, 1);
    return { ok: true };
  },

  async exportQAEntries() {
    if (isLive()) {
      const res = await fetch(`${BASE}/api/qa/export.md`, { headers: adminHeaders() });
      if (!res.ok) throw new Error(`GQ Support API error ${res.status}`);
      return res.text().then(repairMojibake);
    }
    await delay(120);
    return qaEntriesToMarkdown(SEED_QA_ENTRIES);
  },

  async publishQAToAIS() {
    if (isLive()) return adminApi.post('/api/qa/publish');
    await delay(700);
    return { ok: true, ais_source_id: 'verified-qa-' + Date.now(), entries: SEED_QA_ENTRIES.length };
  },

  // Save a verified Q&A from the chat loop directly into the library + AIS index.
  // Uses headers() (not adminHeaders()) so X-User-Id is sent — backend needs it for approved_by.
  async saveVerifiedQA({ conversationId, messageId, question, answer, action, edits, citations, tags }) {
    if (isLive()) return req('/api/qa/save-verified', {
      method: 'POST',
      body: JSON.stringify({
        conversationId, messageId, question, answer, action, edits: edits || null,
        citations: citations || [], tags: tags || [],
      }),
      headers: { 'X-User-Role': _isAdmin ? 'admin' : 'support' },
    });
    await delay(400);
    return {
      ok: true,
      entry_id: 'mock-' + Date.now(),
      indexed_at: new Date().toISOString(),
      message: 'Mock verified — would be saved in real backend.',
    };
  },

  // ----- Escalations -----

  // Raise an L2 escalation from an assistant answer. The backend snapshots the
  // question/answer/citations from messageId and emails the GQ support team.
  async createEscalation({ convId, messageId, note, priority }) {
    if (isLive()) return api.post('/api/escalations', { convId, messageId, note, priority });
    await delay(300);
    const entry = {
      id: 'e' + Date.now(), convId, messageId,
      question: '(mock) escalated question', answer: '(mock) escalated answer',
      citations: [], note: note || '', priority: priority || 'normal',
      status: 'open', createdBy: _userEmail, emailSent: false,
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
    };
    SEED_ESCALATIONS.unshift(entry);
    return clone(entry);
  },

  async listEscalations() {
    if (isLive()) return adminApi.get('/api/escalations');
    await delay(120);
    return clone(SEED_ESCALATIONS);
  },

  async updateEscalation(id, status) {
    if (isLive()) return adminApi.patch(`/api/escalations/${id}`, { status });
    await delay(120);
    const e = SEED_ESCALATIONS.find(x => x.id === id);
    if (e) { e.status = status; e.updatedAt = new Date().toISOString(); }
    return clone(e);
  },

};

export default gqSupportService;
